import React from 'react';
import PropTypes from 'prop-types';
import {
  Center,
  Container,
  GridItem,
  Box,
  useTheme,
  Text,
  Button,
  Circle,
} from '@chakra-ui/react';
import SimpleSlider from '@repo/ui-components/dept/molecules/SimpleSlider';
import Link from '@repo/ui-components/tailwind/Link';
import { DeptGrid } from '@repo/ui-components/dept/design-system';
import AuthorCard from '@repo/ui-components/dept/atoms/AuthorCard';
import Image from '@repo/ui-components/tailwind/Image';
import { useUiComponentsInternationalContext } from '@repo/static-translations';
import { generateSectionId } from '../../../utils/generateSectionId';

function AuthorBox(author) {
  return <Box w="18.875rem">{author && <AuthorCard author={author} />}</Box>;
}

export default function FeaturedBlogAuthors({ block }) {
  const theme = useTheme();
  const { tt } = useUiComponentsInternationalContext();

  return (
    <Box
      bgSize="cover"
      bgRepeat="no-repeat"
      bgImg={theme.images.ellipse}
      backgroundSize={['90%', null, '29%']}
      backgroundPosition={['300% 28%', null, 'right 89%']}
      {...generateSectionId(block)}
    >
      <Container py={[14, null, 16]}>
        <Box overflow="hidden">
          <DeptGrid my={10} flexBasis="100%" gridRowGap={[0, null, 8]} w="full">
            <GridItem colSpan={[6, null, 5]}>
              <Circle as={Center} bg="brand.sunny" h={16} mb={6} w={16}>
                {block?.icon?.asset?.url ? (
                  <Image sanityImage={block?.icon} srcSetBasisWidth={48} />
                ) : (
                  <Image
                    alt={tt(s => s.blocksDeptComponents.expertBadgeAlt)}
                    src={theme.images.inkExpertBadge}
                    width="48px"
                  />
                )}
              </Circle>
              <Text as="h2" textStyle="h2" fontWeight="black" mb={6}>
                {block.headline}
              </Text>
              <Text as="h4" textStyle="h4_5_6" mb={6}>
                {block.copy}
              </Text>
              {block.cta?.text && (
                <Button
                  as={Link}
                  className="text-secondary-foreground"
                  link={block?.cta?.link}
                  variant="secondary"
                >
                  {block.cta.text}
                </Button>
              )}
            </GridItem>
            <GridItem colSpan={[0, null, 1]} />
            <GridItem colSpan={[6, null, 6]} display={['none', null, 'block']}>
              <Image
                className="rounded-lg h-[22.0625rem] w-full [&>img]:w-full"
                ratio={null}
                sanityImage={
                  block.FeaturedBlogAuthorsImage?.image ?? block.media?.image
                }
              />
            </GridItem>
          </DeptGrid>
          <SimpleSlider items={block.people} sliderItem={AuthorBox} />
        </Box>
      </Container>
    </Box>
  );
}

FeaturedBlogAuthors.propTypes = {
  block: PropTypes.shape({
    icon: PropTypes.shape({
      asset: PropTypes.shape({
        url: PropTypes.string,
      }),
    }),
    headline: PropTypes.string,
    copy: PropTypes.string,
    cta: PropTypes.shape({
      text: PropTypes.string,
      link: PropTypes.shape({
        linkType: PropTypes.string,
        href: PropTypes.string,
        internalLink: PropTypes.shape({
          slug: PropTypes.shape({
            current: PropTypes.string,
          }),
        }),
      }),
    }),
    FeaturedBlogAuthorsImage: PropTypes.shape({
      image: PropTypes.shape({
        asset: PropTypes.shape({
          url: PropTypes.string,
        }),
        alt: PropTypes.string,
      }),
    }),
    media: PropTypes.shape({
      image: PropTypes.shape({
        asset: PropTypes.shape({
          url: PropTypes.string,
        }),
        alt: PropTypes.string,
      }),
    }),
    people: PropTypes.arrayOf(
      PropTypes.shape({
        _id: PropTypes.string.isRequired,
        image: PropTypes.shape({
          asset: PropTypes.shape({
            url: PropTypes.string,
          }),
          alt: PropTypes.string,
        }),
        title: PropTypes.string,
        occupation: PropTypes.string,
        slug: PropTypes.shape({
          current: PropTypes.string,
        }),
      })
    ).isRequired,
  }).isRequired,
};
